import { Box, Button, CircularProgress, Tooltip, styled } from '@mui/material';
import type { Actions } from 'features/users';
import { useMyAccess } from 'features/users';
import type { FC } from 'react';
import type { Project } from '../facebook/api/types';
import { observer } from 'mobx-react-lite';
import useRulePageMode from '../hooks/useRulePageMode';

interface RuleUIFormActionBlockProps {
  onDiscard: () => void;
  onSave: () => void;
  isPending: boolean;
  project: Project;
  hasChanges: boolean;
  mode: Extract<Actions, 'update' | 'create' | 'create-to-some'>[];
  discardTitle?: string;
  submitTitle?: string;
}

const RuleUIFormActionStyles = styled(Box)({
  display: 'flex',
  gap: 2,
  borderRadius: '40px',
  position: 'fixed',
  right: 32,
  bottom: 40,
  zIndex: 10,
  backgroundColor: 'white',
  padding: '6px',
  height: 56,
  boxShadow:
    '0px 3px 5px -1px rgba(0, 0, 0, 0.10), 0px 6px 10px 0px rgba(0, 0, 0, 0.07), 0px 1px 18px 0px rgba(0, 0, 0, 0.06)',
});

const RuleUIFormActionBlock: FC<RuleUIFormActionBlockProps> = observer(
  ({ onDiscard, onSave, isPending, project, mode, hasChanges, discardTitle, submitTitle }) => {
    const { isRead } = useRulePageMode();
    const access = useMyAccess(project);

    // @ts-expect-error because not all Actions can be in mode
    if (!access || !access.rules.some((a) => mode.includes(a))) {
      return null;
    }

    return (
      <Tooltip title={isRead ? 'You are viewing rule in read mode' : ''} placement="top">
        <RuleUIFormActionStyles>
          {isPending ? (
            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', height: 36, alignItems: 'center' }}>
              <CircularProgress size={28} sx={(t) => ({ color: t.palette.primary.dark })} />
            </Box>
          ) : (
            <>
              <Button
                sx={{
                  border: 'none',
                  borderRadius: `40px`,
                  '&:hover': { border: 'none' },
                  px: 2,
                }}
                size="medium"
                color="error"
                variant="outlined"
                disabled={!hasChanges || isRead}
                onClick={onDiscard}
              >
                {discardTitle ?? 'Discard'}
              </Button>
              <Button
                sx={{
                  flex: 1,
                  border: 'none',
                  borderRadius: `40px`,
                  '&:hover': { border: 'none' },
                  px: 2,
                }}
                size="medium"
                variant="contained"
                disabled={!hasChanges || isRead}
                onClick={onSave}
              >
                {submitTitle ?? 'Save changes'}
              </Button>
            </>
          )}
        </RuleUIFormActionStyles>
      </Tooltip>
    );
  }
);

export default RuleUIFormActionBlock;
